import React, { ClipboardEventHandler, useCallback, useContext, useEffect, useLayoutEffect } from 'react';
import { useSelector } from 'react-redux';
import * as _ from 'lodash';
import classNames from 'classnames';
import Cookies from 'js-cookie';

import type { FormSubmitEventTarget } from '@/types/dom';
import type { ReduxState } from '@/redux/app/state';
import { useSelectorFactory } from '@/lib/redux/redux-utils';
import { AvoCable } from '@/lib/avo-cable';
import { isChatEnabledSelectorFactory, ticketChannelSelector, ticketStatusSelector } from '@/redux/app/selectors/ticket';
import {
  embeddedVarsEnabledSelector,
  quickChatEnabledSelector,
  quickChatValueSelector,
  spellcheckEnabledSelector,
  templateHotkeysEnabledSelector,
} from '@/redux/app/selectors/settings';

import { UserInfoContext } from '../../../user/user-info';
import { AccountInfoContext } from '../../../account/account-info';

import { OutboundMmsForm, OutboundMmsFormProps } from './OutboundMmsForm';
import { TicketFeed } from './TicketFeed';
import { EmbeddedVarsSelector } from './EmbeddedVarsSelector';
import { TicketFileUploaderContext } from '../ticket-file-uploader.context';

export interface TicketChatboxProps {
  ticket: string;
  rememberToken: string;
  handleSubmit: (
    event: React.FormEvent<HTMLFormElement>&{ target: FormSubmitEventTarget }
  ) => void;
}

export const TicketChatbox = React.memo(
  ({ ticket, handleSubmit, rememberToken }: TicketChatboxProps) => {
    const ticketId = useSelector<ReduxState>((state) => state.ticket.data[ticket]?.id) as string;
    const isChatEnabled = useSelector<ReduxState>(
      useSelectorFactory(isChatEnabledSelectorFactory, [ticket]),
    );
    const channel = useSelector<ReduxState>(_.partial(ticketChannelSelector, ticket));
    const status = useSelector<ReduxState>(_.partial(ticketStatusSelector, ticket));

    const embeddedVarsEnabled = useSelector(embeddedVarsEnabledSelector);
    const templateHotkeysEnabled = useSelector(templateHotkeysEnabledSelector);
    const spellcheckEnabled = useSelector(spellcheckEnabledSelector);
    const quickChatEnabled = useSelector(quickChatEnabledSelector);
    const quickChatValue = useSelector(quickChatValueSelector);
    const textArea: OutboundMmsFormProps['textArea'] = React.useRef();

    const { admin, memberOfAccount, userId } = useContext(UserInfoContext);
    const { name: accountName, hasMacros } = useContext(AccountInfoContext);

    const [users, setUsers] = React.useState({});
    const [typingUsers, setTypingUsers] = React.useState({});
    const [startedTyping, setStartedTyping] = React.useState(false);

    const ticketData = (data) =>
      Object.assign(data, { ticket_id: ticket, remember_token: rememberToken });
    const validUser = (user) => user && user.id && userId !== user.id;
    const sendWSMessage = (tc, data) => tc.send(ticketData(data));

    const addUser = (user) => {
      if (!validUser(user)) return;
      setUsers((prevState) => ({
        ...prevState,
        [user.id]: user,
      }));
    };

    const handleJoin = (data) => {
      addUser(data.user);
      if (startedTyping) {
        sendWSMessage(window.V2TicketChannel, { event: 'typing_start' });
      } else {
        sendWSMessage(window.V2TicketChannel, { event: 'ping' });
      }
    };

    const handleLeave = (data) => {
      if (!validUser(data.user)) return;

      setTypingUsers((prevState) => {
        const newState = { ...prevState };
        delete newState[data.user.id];
        return newState;
      });

      setUsers((prevState) => {
        const newState = { ...prevState };
        delete newState[data.user.id];
        return newState;
      });
    };

    const handleTypingStart = (data) => {
      if (!validUser(data.user)) return;
      setTypingUsers((prevState) => ({ ...prevState, [data.user.id]: data.user }));
    };

    const handleTypingStop = (data) => {
      if (!validUser(data.user)) return;
      setTypingUsers((prevState) => {
        const newState = { ...prevState };
        delete newState[data.user.id];
        return newState;
      });
    };

    const removeDraft = (ticketVal) => {
      sessionStorage.removeItem(`ticket:${ticketVal}`);
    };

    const updateDraft = (ticketVal, value) => {
      if (!value.length) {
        removeDraft(ticketVal);
      } else {
        sessionStorage.setItem(`ticket:${ticketVal}`, `${value}`);
      }
    };

    const handleKeyPress = (e) => {
      updateDraft(ticket, e.target.value);
    };

    const loadDraft = useCallback(() => {
      if (sessionStorage.getItem(`ticket:${ticket}`)) {
        if (textArea.current?.nextSibling.CodeMirror) {
          const cmInstance = textArea.current.nextSibling.CodeMirror;
          cmInstance.setValue(`${sessionStorage.getItem(`ticket:${ticket}`)}`);
        } else if (textArea.current) {
          textArea.current.value = sessionStorage.getItem(`ticket:${ticket}`);
        }
      }
    }, [sessionStorage, textArea, ticket]);

    React.useEffect(() => {
      const typingCheck = setInterval(() => {
        if (textArea.current && textArea.current.value.length > 0) {
          setStartedTyping(true);
          sendWSMessage(window.V2TicketChannel, { event: 'typing_start' });
          return;
        }

        if (!startedTyping) return;

        sendWSMessage(window.V2TicketChannel, { event: 'typing_stop' });
        setStartedTyping(false);
      }, 1000);

      return () => clearInterval(typingCheck);
    }, [textArea, startedTyping]);

    React.useEffect(() => {
      window.V2TicketChannel = AvoCable.subscriptions.create({
        channel: 'V2::TicketChannel',
        ticket_id: ticket,
        remember_token: rememberToken || Cookies.get('remember_token'),
      }, {
        connected: () => console.log('new ticket channel connected'),
        received: (data) => {
          switch (data.event) {
            case 'join':
              handleJoin(data);
              break;
            case 'ping':
              addUser(data.user);
              break;
            case 'leave':
              handleLeave(data);
              break;
            case 'typing_start':
              handleTypingStart(data);
              break;
            case 'typing_stop':
              handleTypingStop(data);
              break;
          }
        },
      });

      return () => {
        window.V2TicketChannel && window.V2TicketChannel.unsubscribe();
        setTypingUsers({});
        setUsers({});
        setStartedTyping(false);
      };
    }, [ticket]);

    const handleEditorClick = (event) => {
      if (event.target.className !== 'info-target') {
        textArea.current?.nextSibling?.CodeMirror?.focus();
      }
    };

    const fileUploader = useContext(TicketFileUploaderContext);
    const handleAddAttachment = () => {
      fileUploader?.triggerUpload();
    };
    const handlePaste: ClipboardEventHandler = (event) => {
      const { types, files } = event.clipboardData;
      if (!types.includes('Files')) return;
      fileUploader?.triggerUpload(Array.from(files));
    };

    const allDependencies = [
      ticket, handleSubmit,
      embeddedVarsEnabled, templateHotkeysEnabled, spellcheckEnabled,
      quickChatEnabled, quickChatValue,
      admin, memberOfAccount,
      ticketId, isChatEnabled, textArea, loadDraft,
    ];
    useLayoutEffect(() => {
      if (isChatEnabled) {
        window.setupChatbox();
        window.scheduler_box.initialize();
        window.ticket_macros.initialize();
        window.listenForMagicWords();
      }
    }, allDependencies);
    useEffect(() => {
      const cmInstance = textArea.current?.nextSibling.CodeMirror;
      loadDraft();
      if (!cmInstance) return undefined;

      window.handleTranslateVars(cmInstance.getValue());

      const changeListener = () => {
        updateDraft(ticket, cmInstance.getValue());
      };
      cmInstance.on('change', changeListener);
      return () => cmInstance.off('change', changeListener);
    }, allDependencies);

    if (isChatEnabled === false) return null;
    const isLoading = typeof isChatEnabled === 'undefined';

    return (
      <div key={ticket} className="chatbox">
        {embeddedVarsEnabled && (
          <>
            <div id="embedded_vars_enabled" />
            {spellcheckEnabled && <div id="spellcheck_enabled" />}
          </>
        )}
        {templateHotkeysEnabled && <div id="template_hotkeys_enabled" />}

        <div id="macros" style={{ display: 'none' }} />

        <div id="scheduler" style={{ display: 'none' }} />
        <form id="message-submit" onSubmit={handleSubmit} className={classNames(status === 'Closed' && channel === 'live_chat' && 'hide-important')}>
          <input type="hidden" name="scheduled_for" className="scheduled-for-input" />
          <input type="hidden" name="cancel_on" className="cancel-on" />
          <div className="inner" onClick={handleEditorClick}>
            <div id="editor" onPaste={handlePaste}>
              <textarea
                id="chat-message"
                name="message"
                ref={textArea}
                className="chat-textarea"
                placeholder={isLoading ? '' : 'Type your message'}
                style={{ height: '23px' }}
                data-macro-count={hasMacros ? 1 : 0}
                data-lpignore="true"
                data-role="none"
                onKeyUp={handleKeyPress}
              />
              {/* <!-- Variable insert form --> */}
              <div className="insert-vars">
                <div className="header">
                  <a className="var-section active" href="#variables" data-ref="variables">Vars</a>
                  <a className="var-section" href="#campaigns" data-ref="campaigns">Campaigns</a>
                </div>
                {/* <!-- variables --> */}
                <EmbeddedVarsSelector ticket={ticket} />
                {/* <!-- campaigns --> */}
                <div className="navigator campaigns">
                  <span className="center ajax-spinner" />
                </div>
              </div>
              {/* <!-- end variable insert form --> */}
              <div className="hints">
                <a href="#">$campaign_</a>
              </div>
            </div>
            <div className="message-buttons">
              {!isLoading && (
                <>
                  <button type="button" id="start-mms" className="info-hover" onClick={handleAddAttachment}>
                    <span className="info-popup">Add Attachment</span>
                  </button>
                  <a id="start-macros" className="info-hover">
                    <span className="info-popup">Insert Template</span>
                  </a>
                  <a id="schedule-text" className="info-hover">
                    <span className="info-popup">Schedule Message</span>
                  </a>
                  {embeddedVarsEnabled && (
                    <a id="custom-vars" className="info-hover">
                      <span className="info-popup">Insert Variable</span>
                    </a>
                  )}
                  <div id="emoji-section" className="info-hover">
                    <span className="info-popup">Insert Emoji</span>
                  </div>
                </>
              )}
            </div>
          </div>
          <TicketFeed
            users={users}
            typingUsers={typingUsers}
          />
          <input
            type="submit"
            disabled={isLoading}
            value=""
            className="submit-button"
            data-role="none"
            data-confirm={admin && !memberOfAccount ? `Admin: Are you sure you want to send this on behalf of ${accountName}?` : null}
            data-quick-chat={quickChatEnabled ? quickChatValue : null}
          />
        </form>
        <OutboundMmsForm
          ticket={ticket}
          handleSubmit={handleSubmit}
          embeddedVarsEnabled={embeddedVarsEnabled}
          textArea={textArea}
        />
      </div>
    );
  },
);
